import styled from '@emotion/styled';
import { Box, Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import {
  CheckCircleOutlined,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import { ExchangeIcon } from 'src/Common/components/ExchangeIcon';
import { Dictionary } from 'lodash';
import {
  ArbitrageAlert,
  ArbitrageCryptoOrderBooks,
  BookByExchange
} from 'src/Modules/Arbitrage/types';
import { useMarkAsReadCrypto } from 'src/Modules/Arbitrage/context';
import { CryptoExchangeInfo } from 'src/Modules/Arbitrage/types';
import { useCallback, useState } from 'react';
import { CryptoIcon } from 'src/Common';

interface Props {
  data: BookByExchange[];
  isLoading: boolean;
  cryptoName: ArbitrageCryptoOrderBooks['name'];
  cryptoSymbol: ArbitrageCryptoOrderBooks['symbol'];
  alertsByCompoundCryptoExchange: Dictionary<ArbitrageAlert>;
  showQuoteCoinPrice: boolean;
}

const ExchangeItem = ({
  exchangeName,
  isMarkedAsRead
}: Pick<CryptoExchangeInfo, 'exchangeName' | 'isMarkedAsRead'>) => {
  return (
    <Box display="flex" alignItems="center">
      <ExchangeIcon
        name={exchangeName}
        style={{ height: '1rem', padding: 0 }}
      />
      {isMarkedAsRead ? (
        <Tooltip color="primary" title="Marked as read">
          <ExchangeName highlighted={isMarkedAsRead}>
            {exchangeName.toUpperCase()}
          </ExchangeName>
        </Tooltip>
      ) : (
        <ExchangeName>{exchangeName.toUpperCase()}</ExchangeName>
      )}
    </Box>
  );
};

const OrderbookLevels = ({ row }: { row: BookByExchange }) => {
  const bids = (row.bids || []).slice(0, 10);
  const asks = (row.asks || []).slice(0, 10);
  const maxLevels = Math.max(bids.length, asks.length);

  if (maxLevels === 0) {
    return (
      <Box p={1} textAlign="center">
        <Typography variant="caption" color="text.secondary">
          No orderbook data available
        </Typography>
      </Box>
    );
  }

  return (
    <OrderbookContainer>
      <OrderbookSide>
        <OrderbookHeader>
          <span>Quantity</span>
          <span>Bid Price</span>
        </OrderbookHeader>
        {bids.map((bid, i) => (
          <OrderbookRow key={i} side="bid">
            <span>{Number(bid.quantity)}</span>
            <span>{Number(bid.price)}</span>
          </OrderbookRow>
        ))}
        {bids.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ p: 0.5 }}>
            No bids
          </Typography>
        )}
      </OrderbookSide>
      <OrderbookSide>
        <OrderbookHeader>
          <span>Ask Price</span>
          <span>Quantity</span>
        </OrderbookHeader>
        {asks.map((ask, i) => (
          <OrderbookRow key={i} side="ask">
            <span>{Number(ask.price)}</span>
            <span>{Number(ask.quantity)}</span>
          </OrderbookRow>
        ))}
        {asks.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ p: 0.5 }}>
            No asks
          </Typography>
        )}
      </OrderbookSide>
    </OrderbookContainer>
  );
};

const getRowId = (r: BookByExchange) => r.exchangeId + r.quoteCoin;

const formatTimestamp = (value: number | undefined) => {
  if (!value) return '-';
  const seconds = Math.round((Date.now() - value) / 1000);
  return seconds < 60 ? `${seconds}s ago` : `${Math.round(seconds / 60)}m ago`;
};

const ExchangeRow = ({
  row,
  isExpanded,
  onToggle,
  onMarkAsRead
}: {
  row: BookByExchange;
  isExpanded: boolean;
  onToggle: () => void;
  onMarkAsRead: () => void;
}) => {
  const hasOrderbook =
    (row.bids?.length ?? 0) > 0 || (row.asks?.length ?? 0) > 0;

  return (
    <ExchangeRowWrapper>
      <RowCells
        onClick={hasOrderbook ? onToggle : undefined}
        clickable={hasOrderbook}
      >
        <Cell width={36}>
          {hasOrderbook && (
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onToggle();
              }}
            >
              {isExpanded ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </IconButton>
          )}
        </Cell>
        <Cell flex={1.2}>
          <ExchangeItem
            isMarkedAsRead={row.isMarkedAsRead}
            exchangeName={row.exchangeId}
          />
        </Cell>
        <Cell flex={0.8}>
          <Box display="flex" alignItems="center">
            <CryptoIcon
              symbol={row.quoteCoin}
              style={{ height: '0.9rem', paddingRight: '0.2rem' }}
            />
            <Label>{row.quoteCoin}</Label>
          </Box>
        </Cell>
        <Cell flex={1}>
          <Mono>{row.topBid ?? '-'}</Mono>
        </Cell>
        <Cell flex={1}>
          <Mono>{row.topAsk ?? '-'}</Mono>
        </Cell>
        <Cell flex={0.8}>
          <Tooltip
            title={
              row.lastOrderbookUpdate
                ? new Date(row.lastOrderbookUpdate).toLocaleTimeString()
                : ''
            }
          >
            <Typography variant="caption">
              {formatTimestamp(row.lastOrderbookUpdate)}
            </Typography>
          </Tooltip>
        </Cell>
        <Cell width={40}>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              onMarkAsRead();
            }}
          >
            <CheckCircleOutlined style={{ fontSize: '0.9rem' }} />
          </IconButton>
        </Cell>
      </RowCells>
      <Collapse in={isExpanded} unmountOnExit>
        <OrderbookPanel>
          <OrderbookLevels row={row} />
        </OrderbookPanel>
      </Collapse>
    </ExchangeRowWrapper>
  );
};

export const ExchangeOrderbookDetailTable = ({
  data,
  isLoading,
  cryptoName,
  cryptoSymbol
}: Props) => {
  const { open: openMarkAsReadDialog } = useMarkAsReadCrypto();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const handleOpenMarkAsReadDialog = useCallback(
    ({ quoteCoin, exchangeId }: BookByExchange) => {
      openMarkAsReadDialog({
        quoteCoin,
        exchange: exchangeId,
        cryptoSymbol,
        name: cryptoName
      });
    },
    [cryptoName, cryptoSymbol, openMarkAsReadDialog]
  );

  const toggleExpand = useCallback((rowId: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px"
      >
        <Typography variant="body2" color="text.secondary">
          No rows
        </Typography>
      </Box>
    );
  }

  return (
    <ScrollContainer>
      <HeaderRow>
        <Cell width={36} />
        <Cell flex={1.2}>
          <HeaderText>Exchange</HeaderText>
        </Cell>
        <Cell flex={0.8}>
          <HeaderText>QuoteCoin</HeaderText>
        </Cell>
        <Cell flex={1}>
          <HeaderText>Top/Bid</HeaderText>
        </Cell>
        <Cell flex={1}>
          <HeaderText>Top/Ask</HeaderText>
        </Cell>
        <Cell flex={0.8}>
          <HeaderText>OB Update</HeaderText>
        </Cell>
        <Cell width={40}>
          <HeaderText>Actions</HeaderText>
        </Cell>
      </HeaderRow>
      {data.map(row => {
        const rowId = getRowId(row);
        return (
          <ExchangeRow
            key={rowId}
            row={row}
            isExpanded={expandedIds.has(rowId)}
            onToggle={() => toggleExpand(rowId)}
            onMarkAsRead={() => handleOpenMarkAsReadDialog(row)}
          />
        );
      })}
    </ScrollContainer>
  );
};

const ScrollContainer = styled.div({
  overflow: 'auto',
  maxHeight: 'calc(100vh - 120px)'
});

const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  padding: '0.4rem 0',
  borderBottom: '1px solid rgba(255,255,255,0.12)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: 'inherit'
});

const HeaderText = styled.span({
  fontSize: '0.65rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  opacity: 0.6
});

const Cell = styled.div<{ flex?: number; width?: number }>(
  ({ flex, width }) => ({
    display: 'flex',
    alignItems: 'center',
    ...(width ? { width, minWidth: width, maxWidth: width } : {}),
    ...(flex ? { flex } : {}),
    padding: '0 0.25rem',
    overflow: 'hidden'
  })
);

const Mono = styled.span({
  fontFamily: 'monospace',
  fontSize: '0.75rem'
});

const ExchangeRowWrapper = styled.div({
  borderBottom: '1px solid rgba(255,255,255,0.06)'
});

const RowCells = styled.div<{ clickable?: boolean }>(({ clickable }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '0.5rem 0',
  cursor: clickable ? 'pointer' : 'default',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.03)'
  }
}));

const OrderbookPanel = styled.div({
  padding: '0.5rem 0.5rem 0.75rem 2.5rem',
  backgroundColor: 'rgba(255,255,255,0.02)',
  borderTop: '1px solid rgba(255,255,255,0.04)'
});

const ExchangeName = styled.span((props: any) => ({
  fontWeight: 700,
  paddingLeft: '0.5rem',
  fontSize: '0.7rem',
  color: props.highlighted
    ? props.theme.palette.primary.light
    : props.theme.palette.text.primary
}));

const Label = styled.span({
  fontWeight: 700,
  paddingLeft: '0.5rem',
  fontSize: '0.7rem'
});

const OrderbookContainer = styled.div({
  display: 'flex',
  gap: '1rem',
  width: '100%'
});

const OrderbookSide = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const OrderbookHeader = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0.25rem 0.5rem',
  fontWeight: 700,
  fontSize: '0.65rem',
  textTransform: 'uppercase',
  opacity: 0.6,
  borderBottom: '1px solid rgba(255,255,255,0.1)'
});

const OrderbookRow = styled.div<{ side: 'bid' | 'ask' }>(({ side, theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0.15rem 0.5rem',
  fontSize: '0.7rem',
  fontFamily: 'monospace',
  color: side === 'bid' ? theme.palette.success.main : theme.palette.error.main,
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.03)'
  }
}));
